import { useEffect, useState, type CSSProperties } from "react";
import { PanelLoading } from "../components/Loading";
import { useTranslation } from "react-i18next";
import { contractsApi, type Contract, type ContractStatus } from "../api/contracts";
import type { ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import { ExportButton } from "../components/ExportButton";
import { Select } from "../components/Select";

const STATUSES: ContractStatus[] = [
  "DRAFT", "SENT_TO_CUSTOMER", "CUSTOMER_VIEWED", "SIGNED", "ACTIVE", "EXPIRED", "CANCELLED",
];

/**
 * Customer-facing contracts list. Uses the same /api/contracts the admin
 * controller does, but RLS + the service-layer scoping mean a CUST_*
 * caller only ever sees their own company's rows.
 */
export default function ContractsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [items, setItems] = useState<Contract[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContractStatus | "">("");
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setItems(await contractsApi.list({ status: statusFilter || undefined }));
    } catch (err) {
      setError((err as ApiError).message);
    }
  }
  useEffect(() => { void load(); }, [statusFilter]);

  async function download(c: Contract) {
    if (!c.latestVersion) return;
    try {
      const r = await contractsApi.presign(c.id, c.latestVersion.id);
      window.open(r.url, "_blank", "noopener,noreferrer");
      // Side-effect on the BE may have flipped status; refresh in the background.
      void load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("contracts.list.title")}</h1>
        </div>
        <div className="page-actions">
          <Select<string>
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as ContractStatus | "")}
            ariaLabel={t("contracts.list.allStatuses")}
            style={{ minWidth: 190 }}
            options={[
              { value: "", label: t("contracts.list.allStatuses") },
              ...STATUSES.map((s) => ({ value: s, label: t(CONTRACT_STATUS_KEY[s] ?? "contracts.status.unknown", s.replace(/_/g, " ")) })),
            ]}
          />
          <ExportButton
            path={`/exports/contracts.csv${statusFilter ? `?status=${statusFilter}` : ""}`}
            label={t("contracts.list.exportCsv")}
            filename="contracts.csv"
          />
        </div>
      </div>

      <section style={card}>
        {error && <div style={errorBox}>{error}</div>}
        {!items ? (
          <PanelLoading text={t("common.loading")} />
        ) : items.length === 0 ? (
          <div style={emptyState}>
            <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("contracts.list.empty")}</p>
          </div>
        ) : (
          <table className="list-card" style={tbl}>
            <thead>
              <tr>
                <th style={th}>{t("contracts.list.col.reference")}</th>
                <th style={th}>{t("contracts.list.col.title")}</th>
                <th style={th}>{t("contracts.list.col.status")}</th>
                <th style={th}>{t("contracts.list.col.effective")}</th>
                <th style={th}>{t("contracts.list.col.expires")}</th>
                <th style={th}>{t("contracts.list.col.latestVersion")}</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td style={td} data-label={t("contracts.list.col.reference")}><span style={mono}>{c.referenceNumber}</span></td>
                  <td style={td} data-label={t("contracts.list.col.title")}>{c.title}</td>
                  <td style={td} data-label={t("contracts.list.col.status")}><StatusBadge status={c.status} /></td>
                  <td style={{ ...td, color: "var(--ink-2)" }} data-label={t("contracts.list.col.effective")}>{c.effectiveDate ? formatDate(c.effectiveDate, i18n.resolvedLanguage) : "—"}</td>
                  <td style={{ ...td, color: "var(--ink-2)" }} data-label={t("contracts.list.col.expires")}>{c.expiresAt ? formatDate(c.expiresAt, i18n.resolvedLanguage) : "—"}</td>
                  <td style={{ ...td, color: "var(--ink-3)" }} data-label={t("contracts.list.col.latestVersion")}>
                    {c.latestVersion
                      ? <span>{t("contracts.list.versionInfo", { version: c.latestVersion.versionNo, kb: (c.latestVersion.sizeBytes / 1024).toFixed(0) })}</span>
                      : <span style={{ color: "var(--ink-4)" }}>{t("contracts.list.noVersion")}</span>}
                  </td>
                  <td style={{ ...td, textAlign: "right" }} className="card-actions">
                    {c.latestVersion && (
                      <button type="button" onClick={() => download(c)} className="btn sm">{t("contracts.list.download")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function formatDate(iso: string, locale?: string) {
  return new Date(iso).toLocaleDateString(locale);
}

/**
 * Stanje ugovora, jednim imenom kroz celu aplikaciju.
 *
 * Stoji ovde a ne u svakom ekranu, jer je prvi prolaz ispisivao sirov enum iz baze — pa je na
 * srpskoj stranici pisalo „EXPIRED".
 */
export const CONTRACT_STATUS_KEY: Record<ContractStatus, string> = {
  DRAFT: "contracts.status.draft",
  SENT_TO_CUSTOMER: "contracts.status.sentToCustomer",
  CUSTOMER_VIEWED: "contracts.status.customerViewed",
  SIGNED: "contracts.status.signed",
  ACTIVE: "contracts.status.active",
  EXPIRED: "contracts.status.expired",
  CANCELLED: "contracts.status.cancelled",
};

export function StatusBadge({ status }: { status: ContractStatus }) {
  const { t } = useTranslation();
  const palette: Record<ContractStatus, { bg: string; fg: string }> = {
    DRAFT:            { bg: "var(--surface-muted)", fg: "var(--ink-2)" },
    SENT_TO_CUSTOMER: { bg: "var(--brand-primary-50)", fg: "var(--brand-primary)" },
    CUSTOMER_VIEWED:  { bg: "var(--tone-cyan-bg)", fg: "var(--tone-cyan)" },
    SIGNED:           { bg: "var(--tone-violet-bg)", fg: "var(--tone-violet)" },
    ACTIVE:           { bg: "var(--ok-bg)", fg: "var(--ok)" },
    EXPIRED:          { bg: "var(--warn-bg)", fg: "var(--warn)" },
    CANCELLED:        { bg: "var(--err-bg)", fg: "var(--err)" },
  };
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  const c = palette[status] ?? { bg: "var(--surface-2)", fg: "var(--ink-2)" };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {t(CONTRACT_STATUS_KEY[status] ?? "contracts.status.unknown", status.replace(/_/g, " "))}
    </span>
  );
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", boxShadow: "var(--shadow-1)", padding: 18,
};
const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left", padding: "10px 8px", color: "var(--ink-3)", fontWeight: 600,
  borderBottom: "1px solid var(--border)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
};
const td: CSSProperties = { padding: "10px 8px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const mono: CSSProperties = { fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-2)" };
const emptyState: CSSProperties = {
  padding: 32, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)", borderRadius: "var(--r-sm)",
  display: "flex", flexDirection: "column", alignItems: "center",
};
const errorBox: CSSProperties = {
  padding: 10, marginBottom: 12, background: "var(--err-bg)", color: "var(--err)",
  borderRadius: "var(--r-sm)", fontSize: 13,
};
